// Discover enums, tables, views, and foreign keys from Drizzle schema
// FEATURE: zen schema discovery and introspection layer
import { type AnyPgColumn, getTableConfig, getViewConfig, type PgTable } from "drizzle-orm/pg-core";
import * as fs from "fs";
import * as ts from "typescript";

import { ENUM_MAP } from "../drizzle/define";
import type { ComputedFieldsConfig, DerivedModelsConfig } from "./codegen-types";
import type { EnumInfo, FKInfo, TableInfo, ViewInfo } from "./types";
import { capitalize, getDrizzleTableName } from "./utils";

export function discoverEnums(allEnums: Record<string, unknown>): Map<string, EnumInfo> {
  const enums = new Map<string, EnumInfo>();
  for (const [exportName, val] of Object.entries(allEnums)) {
    if (typeof val !== "function" || !("enumName" in val)) continue;
    const e = val as any;
    const pgName: string = e.enumName;
    const values: string[] = e.enumValues;
    const zenstackName = capitalize(exportName.replace(/Enum$/, ""));
    const mapping: Record<string, string> | null = e[ENUM_MAP] ?? null;
    enums.set(pgName, { pgName, zenstackName, values, mapping, mappedName: pgName });
  }
  return enums;
}

function getColumnsObject(node: ts.Expression) {
  if (ts.isObjectLiteralExpression(node)) return node;
  if (ts.isArrowFunction(node) || ts.isFunctionExpression(node)) {
    if (ts.isObjectLiteralExpression(node.body)) return node.body;
    if (
      ts.isParenthesizedExpression(node.body) &&
      ts.isObjectLiteralExpression(node.body.expression)
    ) {
      return node.body.expression;
    }
    if (ts.isBlock(node.body)) {
      for (const statement of node.body.statements) {
        if (!ts.isReturnStatement(statement) || !statement.expression) continue;
        if (ts.isObjectLiteralExpression(statement.expression)) return statement.expression;
        if (
          ts.isParenthesizedExpression(statement.expression) &&
          ts.isObjectLiteralExpression(statement.expression.expression)
        ) {
          return statement.expression.expression;
        }
      }
    }
  }
  return null;
}

function getColumnBuilderName(node: ts.Expression): string | null {
  if (ts.isCallExpression(node)) return getColumnBuilderName(node.expression);
  if (ts.isPropertyAccessExpression(node)) {
    if (ts.isIdentifier(node.expression) && node.expression.text === "col") {
      return node.name.text;
    }
    return getColumnBuilderName(node.expression);
  }
  return null;
}

export function assertNoTypedColumnOverrides(tablesFilePath: string) {
  const sourceFile = ts.createSourceFile(
    tablesFilePath,
    fs.readFileSync(tablesFilePath, "utf8"),
    ts.ScriptTarget.Latest,
    true,
    ts.ScriptKind.TS,
  );

  for (const statement of sourceFile.statements) {
    if (!ts.isVariableStatement(statement)) continue;
    for (const declaration of statement.declarationList.declarations) {
      if (!ts.isIdentifier(declaration.name) || !declaration.initializer) continue;
      if (!ts.isCallExpression(declaration.initializer)) continue;

      const columnsArg = declaration.initializer.arguments[1];
      if (!columnsArg) continue;
      const columnsObject = getColumnsObject(columnsArg);
      if (!columnsObject) continue;

      for (const property of columnsObject.properties) {
        if (!ts.isPropertyAssignment(property)) continue;
        const nameNode = property.name;
        const jsKey =
          ts.isIdentifier(nameNode) || ts.isStringLiteral(nameNode) ? nameNode.text : null;
        if (!jsKey) continue;

        let typedCall: ts.CallExpression | null = null;
        const visit = (node: ts.Node) => {
          if (typedCall) return;
          if (
            ts.isCallExpression(node) &&
            ts.isPropertyAccessExpression(node.expression) &&
            node.expression.name.text === "$type"
          ) {
            typedCall = node;
            return;
          }
          ts.forEachChild(node, visit);
        };
        visit(property.initializer);

        if (!typedCall) continue;
        const resolvedCall: ts.CallExpression = typedCall;
        const builderName = getColumnBuilderName((resolvedCall.expression as ts.PropertyAccessExpression).expression);
        if (builderName === "json" || builderName === "jsonb" || builderName === "array") continue;
        // Also allow $type on text columns used with .array() — the chain may resolve differently
        const fullText = property.initializer.getText();
        if (fullText.includes(".array()")) continue;

        console.warn(
          `Warning: Column "${declaration.name.text}.${jsKey}" uses $type<...>() on a ${builderName ?? "scalar"} column. Consider using a real enum.`,
        );
      }
    }
  }
}

export function discoverTables(
  allTables: Record<string, unknown>,
  computedFieldsConfig: ComputedFieldsConfig = {},
  derivedModelsConfig: DerivedModelsConfig = {},
): TableInfo[] {
  const tables: TableInfo[] = [];
  const tablesByModel = new Map<string, TableInfo>();
  for (const [exportName, _val] of Object.entries(allTables)) {
    const val = _val as PgTable;
    if (!val || typeof val !== "object") continue;
    const syms = Object.getOwnPropertySymbols(val);
    if (!syms.some((s) => s.toString().includes("IsDrizzleTable"))) continue;
    const sqlName = getDrizzleTableName(val);
    const modelName = exportName;
    const typeName = capitalize(exportName);
    const sqlToJsKey = new Map<string, string>();
    const jsKeySet = new Set<string>();
    for (const key of Object.keys(val as object)) {
      const col = val[key as keyof PgTable] as AnyPgColumn;
      if (col.name && col.columnType) {
        sqlToJsKey.set(col.name, key);
        jsKeySet.add(key);
      }
    }
    const cfg = getTableConfig(val);
    const computedFields = Object.entries(computedFieldsConfig[exportName] ?? {}).map(
      ([name, field]) => {
        if (jsKeySet.has(name)) {
          throw new Error(
            `Computed field "${exportName}.${name}" collides with an existing column.`,
          );
        }

        return {
          name,
          type: field.type,
          tsType: field.tsType,
        };
      },
    );

    tables.push({
      exportName,
      sqlName,
      modelName,
      typeName,
      table: val as PgTable,
      cfg,
      sqlToJsKey,
      jsKeySet,
      computedFields,
      derivedFrom: null,
      defaultWhere: null,
      narrow: null,
    });
    tablesByModel.set(modelName, tables[tables.length - 1]!);
  }

  for (const modelName of Object.keys(computedFieldsConfig)) {
    if (!tablesByModel.has(modelName)) {
      throw new Error(`Computed fields declared for unknown table export "${modelName}".`);
    }
  }

  for (const [baseModelName, derivedModels] of Object.entries(derivedModelsConfig)) {
    const baseTable = tablesByModel.get(baseModelName);
    if (!baseTable) {
      throw new Error(`Derived models declared for unknown table export "${baseModelName}".`);
    }

    for (const [derivedModelName, declaration] of Object.entries(derivedModels)) {
      if (tablesByModel.has(derivedModelName)) {
        throw new Error(
          `Derived model "${derivedModelName}" collides with an existing table or derived model.`,
        );
      }

      const derivedTable: TableInfo = {
        ...baseTable,
        exportName: derivedModelName,
        modelName: derivedModelName,
        typeName: capitalize(derivedModelName),
        computedFields: [...baseTable.computedFields],
        derivedFrom: baseModelName,
        defaultWhere: declaration.where,
        narrow: declaration.narrow ?? null,
      };

      tables.push(derivedTable);
      tablesByModel.set(derivedModelName, derivedTable);
    }
  }

  return tables;
}

export function discoverViews(allViews: Record<string, unknown>): ViewInfo[] {
  const views: ViewInfo[] = [];
  for (const [exportName, val] of Object.entries(allViews)) {
    if (!val || typeof val !== "object") continue;
    const syms = Object.getOwnPropertySymbols(val);
    if (!syms.some((s) => s.toString().includes("IsDrizzleView"))) continue;

    const cfg = getViewConfig(val as any);
    const sqlName = cfg.name;
    const modelName = exportName;
    const typeName = capitalize(exportName);
    const sqlToJsKey = new Map<string, string>();
    const columns = new Map<string, any>();

    for (const [jsKey, col] of Object.entries(cfg.selectedFields)) {
      const colObj = col as any;
      if (colObj.name && colObj.columnType) {
        sqlToJsKey.set(colObj.name, jsKey);
        columns.set(jsKey, colObj);
      }
    }

    views.push({ exportName, sqlName, modelName, typeName, columns, sqlToJsKey });
  }
  return views;
}

export function extractFKs(table: PgTable): FKInfo[] {
  const cfg = getTableConfig(table);
  return cfg.foreignKeys.map((fk) => {
    const ref = fk.reference();
    return {
      name: fk.getName(),
      localColumns: ref.columns.map((c: AnyPgColumn) => c.name),
      foreignTable: getDrizzleTableName(ref.foreignTable),
      foreignColumns: ref.foreignColumns.map((c: AnyPgColumn) => c.name),
    };
  });
}
